import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Inbox, Lock, LogOut, Loader2 } from "lucide-react";
import { FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bm_admin_token";

const getError = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

export default function AdminEnquiries() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (tok = token) => {
      if (!tok) return;
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/enquiries`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setItems(res.data);
      } catch (e) {
        if (e.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          setError("Session expired. Please log in again.");
        } else {
          setError("Could not load enquiries.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      load(res.data.token);
    } catch (err) {
      setError(getError(err));
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-bm-navy pt-40 pb-24 grain relative">
        <div className="max-w-md mx-auto px-6">
          <FadeIn>
            <form
              data-testid="admin-login-form"
              onSubmit={login}
              className="border border-white/10 bg-white p-8 sm:p-10"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bm-blue/10 text-bm-blue">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-6 font-syne text-3xl font-bold tracking-tight text-bm-ink">
                Team Login
              </h1>
              <p className="mt-2 font-manrope text-sm text-bm-gray">
                Enquiries are for the Brand Mitra 24 team only.
              </p>
              <label className="mt-8 mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">
                Password
              </label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter team password"
                className="w-full rounded-none border border-bm-line bg-white px-4 py-3.5 font-manrope text-sm text-bm-ink outline-none transition-colors duration-300 focus:border-bm-blue placeholder:text-bm-gray/50"
              />
              {error && (
                <p data-testid="admin-login-error" className="mt-3 font-manrope text-sm font-semibold text-bm-red">
                  {error}
                </p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={authLoading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-bm-blue px-8 py-4 font-manrope text-sm font-bold text-white transition-colors duration-300 hover:bg-bm-navy disabled:opacity-60"
              >
                {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "View Enquiries"}
              </button>
            </form>
          </FadeIn>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-enquiries-page" className="min-h-screen bg-bm-surface pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-manrope text-xs font-bold uppercase tracking-[0.3em] text-bm-blue">
              Admin
            </p>
            <h1 className="mt-3 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
              Enquiries
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 rounded-full border border-bm-line bg-white px-5 py-2.5 font-manrope text-sm font-bold text-bm-ink transition-colors duration-300 hover:border-bm-blue"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-bm-line bg-white px-5 py-2.5 font-manrope text-sm font-bold text-bm-ink transition-colors duration-300 hover:border-bm-red hover:text-bm-red"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>

        {error && (
          <p data-testid="admin-error" className="mb-6 font-manrope text-sm font-semibold text-bm-red">
            {error}
          </p>
        )}

        {!loading && items.length === 0 && !error && (
          <div data-testid="admin-empty-state" className="flex flex-col items-center gap-4 border border-dashed border-bm-line bg-white py-24">
            <Inbox className="h-10 w-10 text-bm-gray" />
            <p className="font-manrope text-sm text-bm-gray">
              No enquiries yet. New contact-form submissions will appear here.
            </p>
          </div>
        )}

        {items.length > 0 && (
          <div data-testid="admin-enquiries-table" className="overflow-x-auto border border-bm-line bg-white">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-bm-line bg-bm-navy text-white">
                  {["Date", "Name", "Email", "Phone", "Service", "Budget", "Message"].map((h) => (
                    <th key={h} className="px-5 py-4 font-manrope text-xs font-bold uppercase tracking-widest">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((e, i) => (
                  <tr
                    key={e.id}
                    data-testid={`admin-enquiry-row-${i}`}
                    className="border-b border-bm-line last:border-0 transition-colors duration-200 hover:bg-bm-surface"
                  >
                    <td className="whitespace-nowrap px-5 py-4 font-manrope text-xs text-bm-gray">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-4 font-syne text-sm font-bold text-bm-ink">{e.name}</td>
                    <td className="px-5 py-4 font-manrope text-sm text-bm-ink">{e.email}</td>
                    <td className="px-5 py-4 font-manrope text-sm text-bm-gray">{e.phone || "—"}</td>
                    <td className="px-5 py-4">
                      {e.service ? (
                        <span className="rounded-full bg-bm-blue/10 px-3 py-1 font-manrope text-xs font-bold text-bm-blue">
                          {e.service}
                        </span>
                      ) : (
                        <span className="font-manrope text-sm text-bm-gray">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 font-manrope text-sm text-bm-gray">{e.budget || "—"}</td>
                    <td className="max-w-xs px-5 py-4 font-manrope text-sm text-bm-gray">
                      <span className="line-clamp-2">{e.message}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
